import React, { useState, useCallback, useEffect, useRef } from 'react'
import './style.scss'

import { DrillDownEvent, DrillDownMapState, DrillDownMessage, MetaParams, ParamsEvent } from './types'

import styled from 'styled-components'

import { usePolyline } from './hooks/usePolyline'
import i18n from 'live/services/i18n'

import { useEventEmitter, useSize } from 'ahooks'
import { useMarkers } from './hooks/useMarkers'
import ParametersInformation from './components/ParametersInformation'
import PointInformation from './components/PointInformation'

import DropdownReferenceLine from './components/Dropdowns/DropdownReferenceLine'
import { getApiWebKey } from './api/google-integration'
import { getLookup } from './api/lookups-api'
import { GoogleMapsProvider } from 'plugin-extension-google/shared/GoogleMaps/GoogleMapsProvider'

import MapPolylineWrapper from './components/MapPolylineWrapper'

export type Lookup = {
    key: string
    value: string
}
export type DropdownEmitter = {
    referenceLine?: Lookup
    lookups?: Lookup[]
    tilt?: number
    event?: DrillDownEvent
    reCenter?: boolean
}

const DrillDownMap = (props: LiveReactWidgetImplProps<DrillDownMapState, DrillDownMessage>): JSX.Element => {
    const { state, widget, event } = props
    const { extensionQualifier, lookupParameters } = widget.jsonConfig
    const paramsEvents = state[1]?.data[0] as ParamsEvent // Before some data came via 'meta' params now it comes directly in the event
    const eventEmitter$ = useEventEmitter<DropdownEmitter>()
    const wrapperContentRef = useRef(null)

    const [lookups, setLookups] = useState<Lookup[]>([]) // Lookups that are active on screen
    const [lookupEntries, setLookupEntries] = useState<Lookup[]>([]) // List of lookups that was load with fetch in the LookupApi
    const [map, setMap] = useState<google.maps.Map>()
    const [referenceLine, setReferenceLine] = useState<Lookup>()
    const [eventActive, setEventActive] = useState<DrillDownEvent>()
    const [googleMapsApiKey, setGoogkeMapsApiKey] = useState<string>('')

    const size = useSize(wrapperContentRef)

    const onLoadMap = useCallback(map => {
        setMap(map)
    }, [])

    const { createMarkers } = useMarkers(map)
    const { createPolyline, changeVisiblePolylines, selectPolyline } = usePolyline(map, eventEmitter$)

    useEffect(() => {
        getApiWebKey(extensionQualifier).then(res => {
            setGoogkeMapsApiKey(res)
        })
    }, [extensionQualifier])

    useEffect(() => {
        if (lookupParameters) {
            getLookup(lookupParameters).then(res => {
                const entries = res?.entries || []
                setLookupEntries(entries)
            })
        }
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const data = state[0].data || []
        if (data.length > 0) {
            const lastIndex = data.length > 0 ? data.length - 1 : 0
            const startMarker = { event: data[0], url: '/content/plugin-drill-down-map/Start.svg', zIndex: 2 }
            const endMarker = { event: data[lastIndex], url: '/content/plugin-drill-down-map/End.svg', zIndex: 2 }
            const gradientColorStart = paramsEvents?.gradientColorStart || '#aaaaaa'
            const gradientColorEnd = paramsEvents?.gradientColorEnd || '#000000'

            createMarkers([startMarker, endMarker])
            createPolyline(data, gradientColorStart, gradientColorEnd)

            if (!eventActive || (data[0]?.timestamp > state.span.start && data[0]?.timestamp > eventActive?.timestamp))
                eventEmitter$.emit({ event: data[0] })
            else eventEmitter$.emit({ event: eventActive, reCenter: false })

            // When a new event arrives, the 'createPolyline()' method creates a new polyline, so we need to check if there is a 'referenceLine' to reapply.
            if (referenceLine) changeVisiblePolylines(referenceLine.key, gradientColorEnd)
        }
    }, [state, event, paramsEvents]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        changeVisiblePolylines(referenceLine?.key, paramsEvents?.gradientColorEnd || '#000000')
    }, [referenceLine]) // eslint-disable-line react-hooks/exhaustive-deps

    eventEmitter$.useSubscription(({ event, lookups, referenceLine }) => {
        if (event) {
            setEventActive(event)
            selectPolyline(String(event?.id))
        }
        if (lookups) setLookups(lookups)

        if (referenceLine) changeVisiblePolylines(referenceLine?.key)
    })

    return (
        <Wrapper>
            {state[0]?.data?.length === 0 && (
                <WrapperEmptyData>
                    <div className="content-empty-data">
                        <div style={{ marginRight: '12px' }}>
                            <i className="far fa-route"></i>
                        </div>

                        <div>
                            <span style={{ fontWeight: 800 }}>{i18n('Select a trip')}</span>
                            <span> {i18n('from the list to see its information')}</span>
                        </div>
                    </div>
                </WrapperEmptyData>
            )}

            <ParametersInformation
                map={map}
                title={paramsEvents?.title}
                widgetProps={props}
                events={state[0].data}
                paramsEvents={paramsEvents}
                referenceLine={referenceLine}
                lookupEntries={lookupEntries}
                emitter={eventEmitter$}
                selectPolyline={selectPolyline}
            />

            <WrapperContent ref={wrapperContentRef}>
                <PointInformation
                    height={size?.height ?? 0}
                    allEvents={state[0]?.data}
                    activeEvent={eventActive}
                    extensionQualifier={extensionQualifier}
                />

                {googleMapsApiKey && (
                    <GoogleMapsProvider zoom={10} googleMapsApiKey={googleMapsApiKey}>
                        <MapPolylineWrapper onLoadMap={onLoadMap} />
                        <WrapperActions>
                            <DropdownReferenceLine lookups={lookups} callback={setReferenceLine} />
                            {/* <LayersMenu mapRef={map} emitter={eventEmitter$}/> */}
                            {/* TODO */}
                            {/* <ButtonFullScreen callback={() => setIsFullScreen(!isFullScreen)} /> */}
                        </WrapperActions>
                    </GoogleMapsProvider>
                )}
            </WrapperContent>
        </Wrapper>
    )
}

export default React.memo(DrillDownMap)

const Wrapper = styled.div`
    display: flex;
    height: 100%;
    flex-direction: column;
`

const WrapperContent = styled.div`
    display: flex;
    flex: 1;
    overflow: hidden;
`

const WrapperActions = styled.div`
    display: flex;
    padding-left: 10px;
    justify-content: end;
    z-index: 1;
    position: relative;
    top: 36px;
    right: 68px;
    position: absolute;
`

const WrapperEmptyData = styled.div`
    height: 100%;
    width: 100%;
    padding-top: 10%;
    position: absolute;
    z-index: 3;
    display: flex;
    justify-content: center;
    background: rgba(255, 255, 255, 0.582);

    .content-empty-data {
        height: 69px;
        width: 241px;
        display: flex;
        margin: 0 10px;
        font-size: 13px;
        font-weight: 400;
        padding: 8px 16px;
        border-radius: 2px;
        align-items: center;
        justify-content: flex-start;
        color: ${({ theme }) => theme['gray-700']};
        background: ${({ theme }) => theme['gray-0']};
        font-family: ${({ theme }) => theme['font-regular']};
        border: 1px solid ${({ theme }) => theme['gray-400']};
    }

    i {
        color: ${({ theme }) => theme['gray-500']};
        font-weight: 400;
        font-size: 32px;
    }
`
